import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import FacebookPixel from 'react-facebook-pixel';
import { initAmplitude, initGoogleTag, initLockerStudio, sendAmplitudeData } from '../../analytics';
import {
  EVENTS,
  ENV,
  COOKIE_UUID_KEY,
  FB_PIXEL_ID,
  GTM_ID,
  HOTJAR_ID,
  IS_DEV,
  IS_PROD,
  AF_SMART_SCRIPT_KEY,
} from '../constants';
import { getGtmScript, hotjar, oneLinkSmartScript, tiktok } from '../scripts';
import { useCookieYesDisabled } from './useCookieYesDisabled';
import { queryParser, useInlineScript } from '@applyft-web/utils';

const GTM_LOAD_TIMEOUT_MS = 10_000;

export const useInitAnalytics = () => {
  const { fbclid, ttclid } = queryParser(window.location.search);
  const tiktokScript = ttclid ? tiktok : '';
  const hotjarScript = HOTJAR_ID ? hotjar : '';
  const isCookieYesDisabled = useCookieYesDisabled();
  const gtmScript = getGtmScript(isCookieYesDisabled);
  const initCallback = (analytics: string) => {
    window.sessionStorage?.setItem(`${analytics}Init`, 'true');
  };
  const inited = useRef(false);
  const amplitudeInited = useRef(false);
  const isGtmLoaded = useInlineScript(gtmScript, 'google_tag_manager');
  const isWaitingForGtm = Boolean(GTM_ID) && !isGtmLoaded;

  useEffect(() => {
    if (inited.current || isWaitingForGtm) return;
    initGoogleTag();
    inited.current = true;
  }, [isWaitingForGtm]);

  useEffect(() => {
    if (!isWaitingForGtm) return;

    const timeoutId = setTimeout(() => {
      if (!IS_PROD) {
        console.error(
          `GTM container ${GTM_ID} did not load within ${GTM_LOAD_TIMEOUT_MS}ms; Google Analytics and Google Ads are not initialized`,
        );
      }
      // TODO: move to Sentry once error monitoring is added
      sendAmplitudeData(EVENTS.GTM_LOAD_TIMEOUT, {
        gtm_id: GTM_ID,
        timeout_ms: GTM_LOAD_TIMEOUT_MS,
        page_path: window.location.pathname,
      });
    }, GTM_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isWaitingForGtm]);

  useEffect(() => {
    if (GTM_ID || IS_DEV) return;

    if (!IS_PROD) {
      console.error(
        'REACT_APP_GTM_ID is not set; the cookie banner is not loaded and Google Analytics and Google Ads start without consent defaults',
      );
    }
    // TODO: move to Sentry once error monitoring is added
    sendAmplitudeData(EVENTS.GTM_NOT_CONFIGURED, {
      env: ENV,
      page_path: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    if (amplitudeInited.current) return;
    amplitudeInited.current = true;
    initAmplitude(undefined, initCallback);
  }, []);

  useEffect(() => {
    const checkCookies = setInterval(() => {
      const id = Cookies.get(COOKIE_UUID_KEY);
      if (id) {
        initLockerStudio(id, initCallback);
        if (fbclid) {
          // @ts-ignore
          FacebookPixel.init(FB_PIXEL_ID, { external_id: id });
          FacebookPixel.pageView();
        }
        clearInterval(checkCookies);
      }
    }, 500);

    const timeoutId = setTimeout(() => {
      clearInterval(checkCookies);
    }, 30_000);

    return () => {
      clearInterval(checkCookies);
      clearTimeout(timeoutId);
    };
  }, [fbclid]);

  useInlineScript(tiktokScript, 'TiktokJelly');

  useInlineScript(hotjarScript, 'hj');

  useInlineScript(oneLinkSmartScript, AF_SMART_SCRIPT_KEY);
};
